/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2019, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
*/

import { InjectionToken } from '@angular/core';

/**
 * This file contains providers/factory methods for AngularJS services, so they can be used via Angular.
 */

// ---------------------------------------------------------------------------
/**
 * Get $rootScope to use in our Angular app.
 */
function rootScopeFactory(i: any) {
  return i.get('$rootScope');
}

export const RootScopeProvider = {
  provide: '$rootScope',
  useFactory: rootScopeFactory,
  deps: ['$injector'],
};

// ---------------------------------------------------------------------------
/**
 * Get $anchorScroll to use in our Angular app.
 */
function anchorScrollServiceFactory(i: any) {
  return i.get('$anchorScroll');
}

export const AnchorScrollProvider = {
  provide: '$anchorScroll',
  useFactory: anchorScrollServiceFactory,
  deps: ['$injector'],
};

// ---------------------------------------------------------------------------
/**
 * Get our BulbAutoSaveService to use in our Angular app.
 */
function autoSaveServiceFactory(i: any) {
  return i.get('BulbAutoSaveService');
}

export const BulbAutoSaveServiceProvider = {
  provide: 'BulbAutoSaveService',
  useFactory: autoSaveServiceFactory,
  deps: ['$injector'],
};

// ---------------------------------------------------------------------------
/**
 * Get our BulbActivityService to use in our Angular app.
 */
function activityServiceFactory(i: any) {
  return i.get('BulbActivityService');
}

export const BulbActivityServiceProvider = {
  provide: 'BulbActivityService',
  useFactory: activityServiceFactory,
  deps: ['$injector'],
};

// ---------------------------------------------------------------------------
/**
 * Get BulbAuthService to use in angular app.
 * @param i
 */
function authServiceFactory(i: any) {
  return i.get('BulbAuthService');
}

export const BulbAuthServiceProvider = {
  provide: 'BulbAuthService',
  useFactory: authServiceFactory,
  deps: ['$injector'],
};

// ---------------------------------------------------------------------------
/**
 * Get BulbPresentationService to use in angular app.
 * @param i
 */
function presentationServiceFactory(i: any) {
  return i.get('BulbPresentationService');
}

export const BulbPresentationServiceProvider = {
  provide: 'BulbPresentationService',
  useFactory: presentationServiceFactory,
  deps: ['$injector'],
};
// ---------------------------------------------------------------------------
/**
 * Get BulbAjax service to use in Angular app.
 * @param i AngularJS $injector.
 */
function ajaxServiceFactor(i: any) {
  return i.get('BulbAjaxService');
}

export const BulbAjaxServiceProvider = {
  provide: 'BulbAjaxService',
  useFactory: ajaxServiceFactor,
  deps: ['$injector'],
};

// ---------------------------------------------------------------------------
/**
 * Get BulbAuthService to use in angular app.
 *
 * @param i AngularJS $injector service.
 */
function pageServiceFactory(i: any) {
  return i.get('BulbPageService');
}

export const BulbPageServiceProvider = {
  provide: 'BulbPageService',
  useFactory: pageServiceFactory,
  deps: ['$injector'],
};

// ---------------------------------------------------------------------------
/**
 * Get BulbUnitService to use in angular app.
 *
 * @param i AngularJS $injector service.
 */
function unitServiceFactory(i: any) {
  return i.get('BulbUnitService');
}

export const BulbUnitServiceProvider = {
  provide: 'BulbUnitService',
  useFactory: unitServiceFactory,
  deps: ['$injector'],
};

// ---------------------------------------------------------------------------
/**
 * Get our BulbAssetLibraryService from AngularJS to use in the Angular framework.
 *
 * @param injector AngularJS injector
 */
function assetlibraryServiceFactory(injector: any) {
  return injector.get('BulbAssetLibraryService');
}

export const BulbAssetLibraryServiceProvider = {
  provide: 'BulbAssetLibraryService',
  useFactory: assetlibraryServiceFactory,
  deps: ['$injector'],
};

// ---------------------------------------------------------------------------
/**
 * Get our BulbAssetLibraryUploadService from AngularJS to use in the Angular framework.
 *
 * @param injector AngularJS injector
 */
function assetlibraryUploadServiceFactory(injector: any) {
  return injector.get('BulbAssetLibraryUploadService');
}

export const BulbAssetLibraryUploadServiceProvider = {
  provide: 'BulbAssetLibraryUploadService',
  useFactory: assetlibraryUploadServiceFactory,
  deps: ['$injector'],
};

// ---------------------------------------------------------------------------
/**
 * Get our BulbAssetLibraryUnsplashService from AngularJS to use in the Angular framework.
 *
 * @param injector AngularJS injector
 */
function assetLibraryUnsplashServiceFactory(injector: any) {
  return injector.get('BulbAssetLibraryUnsplashService');
}

export const BulbAssetLibraryUnsplashServiceProvider = {
  provide: 'BulbAssetLibraryUnsplashService',
  useFactory: assetLibraryUnsplashServiceFactory,
  deps: ['$injector'],
};

// ---------------------------------------------------------------------------
export const BULB_BUSY_COVER_SERVICE = new InjectionToken<any>('BulbBusyCoverService');

/**
 * Get our BulbBusyCoverService from AngularJS to use in the Angular framework.
 *
 * @param injector AngularJS injector
 */
function busyCoverServiceFactory(injector: any) {
  return injector.get('BulbBusyCoverService');
}

export const BulbBusyCoverServiceProvider = {
  provide: BULB_BUSY_COVER_SERVICE,
  useFactory: busyCoverServiceFactory,
  deps: ['$injector'],
};

// ---------------------------------------------------------------------------
/**
 * Get our BulbGroupService to use in angular app.
 * @param i
 */
function groupServiceFactory(i: any) {
  return i.get('BulbGroupService');
}

export const BulbGroupServiceProvider = {
  provide: 'BulbGroupService',
  useFactory: groupServiceFactory,
  deps: ['$injector'],
};

// ---------------------------------------------------------------------------
function locationServiceFactory(i: any) {
  return i.get('$location');
}

export const LocationProvider = {
  provide: '$location',
  useFactory: locationServiceFactory,
  deps: ['$injector'],
};

// ---------------------------------------------------------------------------
/**
 * Get BulbModalService to use in angular app.
 * @param i
 */
function modalServiceFactory(i: any) {
  return i.get('BulbModalService');
}

export const BulbModalServiceProvider = {
  provide: 'BulbModalService',
  useFactory: modalServiceFactory,
  deps: ['$injector'],
};

// ---------------------------------------------------------------------------
/**
 * Get BulbErrorService to use in angular app.
 * @param i
 */
function errorServiceFactory(i: any) {
  return i.get('BulbErrorService');
}

export const BulbErrorServiceProvider = {
  provide: 'BulbErrorService',
  useFactory: errorServiceFactory,
  deps: ['$injector'],
};

/**
 * Get PushNotificationService to use in angular app.
 * @param i
 */
function pushNotificationServiceFactory(i: any) {
  return i.get('PushNotificationService');
}

export const PushNotificationServiceProvider = {
  provide: 'PushNotificationService',
  useFactory: pushNotificationServiceFactory,
  deps: ['$injector'],
};

// ---------------------------------------------------------------------------
/**
 * Get BulbBrowserService to use in angular app.
 * @param i
 */
function browserServiceFactory(i: any) {
  return i.get('BulbBrowserService');
}

export const BulbBrowserServiceProvider = {
  provide: 'BulbBrowserService',
  useFactory: browserServiceFactory,
  deps: ['$injector'],
};

// ---------------------------------------------------------------------------
/**
 * Get Restangular to use in angular app.
 * @param i
 */
function restangularFactory(i: any) {
  return i.get('Restangular');
}

export const RestangularProvider = {
  provide: 'Restangular',
  useFactory: restangularFactory,
  deps: ['$injector'],
};

// ---------------------------------------------------------------------------
/**
 * Get $translate to use in angular app.
 * @param i
 */
function translateFactory(i: any) {
  return i.get('$translate');
}

export const TranslateProvider = {
  provide: '$translate',
  useFactory: translateFactory,
  deps: ['$injector'],
};

// ---------------------------------------------------------------------------
/**
 * Get $state from AngularJS to use in the Angular framework.
 *
 * @param injector AngularJS injector
 */
function stateFactory(injector: any) {
  return injector.get('$state');
}

export const StateProvider = {
  provide: '$state',
  useFactory: stateFactory,
  deps: ['$injector'],
};

// ---------------------------------------------------------------------------
/**
 * Get our BulbAssetLibraryOneDriveService from AngularJS to use in the Angular framework.
 *
 * @param injector AngularJS injector
 */
function assetlibraryOneDriveServiceFactory(injector: any) {
  return injector.get('BulbAssetLibraryOneDriveService');
}

export const BulbAssetLibraryOneDriveServiceProvider = {
  provide: 'BulbAssetLibraryOneDriveService',
  useFactory: assetlibraryOneDriveServiceFactory,
  deps: ['$injector'],
};

/**
 * Get ASSET_LIBRARY from AngularJS to use in the Angular framework.
 *
 * @param injector AngularJS injector
 */
export function assetLibraryFactory(injector : any) {
  return injector.get('ASSET_LIBRARY');
}

export const AssetLibraryProvider = {
  provide: 'ASSET_LIBRARY',
  useFactory: assetLibraryFactory,
  deps: ['$injector'],
};
